import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import useQRCodeGenerator from "react-hook-qrcode-svg";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { enableTwoFactor } from "src/store/account";
import { getTwoFactorSettings } from "src/ui/containers/account/api";
import { makeStyles } from "tss-react/mui";

import { Box, Divider, Theme, Typography } from "@mui/material";
import { LoadingIcon } from "@shared/Loading";
import OtpSection from "@shared/otp/OtpSection";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme: Theme) => ({
    codeBox: { minHeight: 300, display: "flex", justifyContent: "center", alignItems: "center" },
    // White frame + quiet zone: GA and other scanners need contrast on dark theme.
    qrCodeSurface: {
        display: "inline-flex",
        colorScheme: "light",
        forcedColorAdjust: "none",
        boxSizing: "border-box",
        padding: theme.spacing(2),
        backgroundColor: "#ffffff",
        borderRadius: theme.shape.borderRadius,
    },
    indentation: { marginLeft: theme.spacing(9), padding: theme.spacing(2, 0) },
    formContainer: {
        width: "100%",
    },
}));

const qrCodeSize = 300;
const qrCodeLevel = "L";
const qrCodeBorder = 1;
// Theme-based fills flip in dark mode and break scanning.
const qrCodeBackgroundFill = "#ffffff";
const qrCodeModuleFill = "#000000";

type FormValues = {
    secretKey: string;
    code: string;
};

type TwoFactorEnableFormProps = {
    onSuccess: () => void;
    onCancel: () => void;
};

export const TwoFactorEnableForm = ({ onSuccess }: TwoFactorEnableFormProps) => {
    const [loading, setLoading] = useState(true);
    const [qrCode, setQrCode] = useState("");
    const [secretKey, setSecretKey] = useState<string>();
    const { path, viewBox } = useQRCodeGenerator(qrCode, qrCodeLevel, qrCodeBorder);
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);

    useEffect(() => {
        setLoading(true);
        void (async () => {
            try {
                const data = await getTwoFactorSettings();
                setSecretKey(data.secretKey);
                setQrCode(data.url);
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const onSubmit = async (values: FormValues) => {
        setLoading(true);
        try {
            const { message } = await dispatch(enableTwoFactor({ code: values.code, secretKey })).unwrap();
            showSuccessToast(message);
            onSuccess();
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setLoading(false);
        }
    };

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        if (!values.code) {
            errors.code = t("pages:twoFactorEnablePage.validation.codeRequired");
        }
        if (values.code && /[a-zA-Z]/g.test(values.code)) {
            errors.code = t("pages:twoFactorEnablePage.validation.badFormat");
        }
        return errors;
    };

    if (!viewBox) {
        return null;
    }

    return (
        <SectionPanel title={t("pages:twoFactorEnablePage.title")}>
            <Box>
                <ol>
                    <li className={classes.indentation}>
                        <Typography>
                            <Trans
                                ns="pages"
                                i18nKey="twoFactorEnablePage.step1.fullText"
                                components={{
                                    0: <i />,
                                    1: <span />,
                                    2: (
                                        <a
                                            href="https://go.microsoft.com/fwlink/?Linkid=825071"
                                            rel="nofollow noreferrer"
                                            target="_blank"
                                        />
                                    ),
                                    3: (
                                        <a
                                            href="https://go.microsoft.com/fwlink/?Linkid=825072"
                                            rel="nofollow noreferrer"
                                            target="_blank"
                                        />
                                    ),
                                    4: (
                                        <a
                                            href="https://go.microsoft.com/fwlink/?Linkid=825073"
                                            rel="nofollow noreferrer"
                                            target="_blank"
                                        />
                                    ),
                                    5: <i />,
                                    6: (
                                        <a
                                            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
                                            rel="nofollow noreferrer"
                                            target="_blank"
                                        />
                                    ),
                                    7: (
                                        <a
                                            href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
                                            rel="nofollow noreferrer"
                                            target="_blank"
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                    </li>
                    <li className={classes.indentation}>
                        <Typography>{t("pages:twoFactorEnablePage.step2.description")}</Typography>
                    </li>
                    <li className={classes.indentation}>
                        <Typography>{t("pages:twoFactorEnablePage.step3.scanQr")}</Typography>
                        <div className={classes.codeBox}>
                            {loading ? (
                                <LoadingIcon size="block" />
                            ) : (
                                <div className={classes.qrCodeSurface}>
                                    <svg width={qrCodeSize} height={qrCodeSize} viewBox={viewBox} stroke="none">
                                        <rect width="100%" height="100%" fill={qrCodeBackgroundFill} />
                                        <path d={path} fill={qrCodeModuleFill} />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </li>
                    <li className={classes.indentation}>
                        <Typography>{t("pages:twoFactorEnablePage.step4.description")}</Typography>
                    </li>
                    <li className={classes.indentation}>
                        <Typography>{t("pages:twoFactorEnablePage.step5.description")}</Typography>
                    </li>
                </ol>
            </Box>
            <Divider />
            <Box>
                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={onValidate}
                    render={({ handleSubmit, form, submitting }) => (
                        <form onSubmit={(e) => void handleSubmit(e)} className={classes.formContainer}>
                            <OtpSection
                                setupOtp
                                submitting={submitting}
                                onSubmit={(otp) => {
                                    form.change("code", otp);
                                    void handleSubmit();
                                }}
                            />
                        </form>
                    )}
                />
            </Box>
        </SectionPanel>
    );
};
